import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed,
  inject,
  ViewChild,
  ElementRef,
  signal,
  forwardRef,
  OnDestroy,
} from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Overlay, OverlayRef, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ViewContainerRef, TemplateRef } from '@angular/core';
import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { ThemeService } from '../services/theme.service';
import { IconGlassComponent } from '../icon/icon-glass.component';
import { SelectOption, SelectVariant, SelectSize } from '../models/select.model';

export { SelectOption, SelectVariant, SelectSize } from '../models/select.model';

const selectVariants = cva({
  base: [
    'relative w-full rounded-xl border transition-all duration-300 ease-out',
    'cursor-pointer flex items-center',
    'focus-within:ring-2 focus-within:ring-primary-500/50 focus-within:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    // Glass morphism base
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-60 before:rounded-xl before:pointer-events-none before:-z-10 before:backdrop-blur-md',
  ].join(' '),
  variants: {
    variant: {
      default: [
        'bg-linear-to-b from-slate-100/95 to-slate-200/92 border-slate-400/35 text-slate-700',
        'hover:from-white hover:to-slate-200/96 hover:border-slate-500/40',
        'focus-within:from-white focus-within:to-slate-200/98 focus-within:border-primary-400/45',
        'dark:from-[#1c2e4a]/72 dark:to-[#101d31]/84 dark:border-slate-200/25 dark:text-slate-100',
        'dark:hover:from-[#243a5c]/80 dark:hover:to-[#16263f]/90 dark:hover:border-slate-100/40',
        'dark:focus-within:from-[#243a5c]/84 dark:focus-within:to-[#16263f]/94 dark:focus-within:border-primary-300/50',
        'shadow-[0_4px_6px_-1px_rgba(2,8,23,0.20),0_2px_4px_-2px_rgba(2,8,23,0.20)]',
      ].join(' '),
      elevated: [
        'bg-linear-to-b from-slate-100/98 to-slate-200/95 border-primary-300/40 text-slate-700',
        'hover:from-white hover:to-slate-100/98 hover:border-primary-400/48',
        'focus-within:from-white focus-within:to-slate-100/98 focus-within:border-primary-400/55',
        'dark:from-[#1f3554]/78 dark:to-[#13233a]/90 dark:border-primary-200/35 dark:text-slate-50',
        'dark:hover:from-[#274165]/84 dark:hover:to-[#182b46]/94 dark:hover:border-primary-100/48',
        'dark:focus-within:from-[#274165]/88 dark:focus-within:to-[#182b46]/96 dark:focus-within:border-primary-300/55',
        'shadow-[0_10px_24px_rgba(2,8,23,0.18)]',
      ].join(' '),
      premium: [
        // Ultra-premium for e-commerce
        // Light theme
        'bg-linear-to-r from-white/25 to-primary-50/20 border-primary-300/40 text-gray-800',
        'shadow-xl shadow-primary-500/15',
        'hover:from-white/30 hover:to-primary-50/30 hover:border-primary-300/60',
        'hover:shadow-2xl hover:shadow-primary-500/25',
        // Dark theme
        'dark:from-white/15 dark:to-primary-900/20 dark:border-primary-400/30 dark:text-gray-100',
        'dark:shadow-xl dark:shadow-primary-500/20',
        'dark:hover:from-white/20 dark:hover:to-primary-900/30',
      ].join(' '),
      minimal: [
        // Light theme
        'bg-white/10 border-gray-200/20 text-gray-600',
        'hover:bg-white/15 hover:border-gray-200/30',
        // Dark theme
        'dark:bg-white/5 dark:border-gray-600/15 dark:text-gray-300',
        'dark:hover:bg-white/8 dark:hover:border-gray-600/25',
      ].join(' '),
    },
    size: {
      sm: 'h-9 px-3 text-sm',
      md: 'h-11 px-4 text-base',
      lg: 'h-12 px-5 text-lg',
      xl: 'h-14 px-6 text-xl',
    },
    state: {
      default: '',
      error: 'border-red-400/50 focus-within:border-red-500/60 focus-within:ring-red-500/50',
      success: 'border-cyan-400/50 focus-within:border-cyan-500/60 focus-within:ring-cyan-500/50',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    state: 'default',
  },
});

const dropdownVariants = cva({
  base: [
    'w-full max-h-60 overflow-hidden rounded-xl border',
    'shadow-2xl',
    'transition-all duration-300 ease-out transform-gpu',
    'relative',
    // Ultra-modern glass effects
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-80 before:rounded-xl before:pointer-events-none before:-z-10 before:backdrop-blur-2xl',
  ].join(' '),
  variants: {
    variant: {
      default: [
        'bg-linear-to-b from-white/98 to-slate-100/96 border-slate-300/55 shadow-[0_18px_36px_rgba(2,8,23,0.14)]',
        'dark:from-[#243a5c]/92 dark:to-[#13233a]/96 dark:border-slate-200/22 dark:shadow-[0_18px_36px_rgba(2,8,23,0.34)]',
      ].join(' '),
      elevated: [
        'bg-linear-to-b from-white/98 to-slate-100/96 border-primary-300/45 shadow-[0_20px_40px_rgba(2,8,23,0.16)]',
        'dark:from-[#274165]/94 dark:to-[#16263f]/98 dark:border-primary-200/28 dark:shadow-[0_20px_40px_rgba(2,8,23,0.38)]',
      ].join(' '),
      premium: [
        'bg-linear-to-br from-white/80 via-white/70 to-primary-50/40',
        'border-primary-200/60 shadow-2xl shadow-primary-500/25',
        'dark:from-white/20 dark:via-white/10 dark:to-primary-900/30',
        'dark:border-primary-400/40 dark:shadow-primary-500/35',
      ].join(' '),
      minimal: [
        'bg-linear-to-b from-white/96 to-slate-100/94 border-slate-300/45 shadow-[0_14px_28px_rgba(2,8,23,0.12)]',
        'dark:from-[#223652]/88 dark:to-[#122034]/94 dark:border-slate-200/18 dark:shadow-[0_14px_28px_rgba(2,8,23,0.24)]',
      ].join(' '),
    },
    state: {
      closed: 'opacity-0 scale-95 pointer-events-none -translate-y-2',
      open: 'opacity-100 scale-100 pointer-events-auto translate-y-0',
    },
  },
  defaultVariants: {
    variant: 'default',
    state: 'closed',
  },
});

const optionVariants = cva({
  base: [
    'relative flex items-center px-4 py-3 cursor-pointer transition-all duration-200 ease-out',
    'hover:bg-white/20 dark:hover:bg-white/10',
    'focus:bg-white/20 dark:focus:bg-white/10 focus:outline-none',
    'group backdrop-blur-sm',
    // 3D hover effect
    'hover:translate-x-1 active:scale-98',
  ].join(' '),
  variants: {
    selected: {
      true: 'bg-primary-100/50 dark:bg-primary-800/30 text-primary-700 dark:text-primary-200 font-medium',
      false: 'text-slate-700 dark:text-gray-100',
    },
    disabled: {
      true: 'opacity-50 cursor-not-allowed pointer-events-none',
      false: '',
    },
    focused: {
      true: 'bg-slate-200/70 dark:bg-white/15 translate-x-1 ring-1 ring-primary-400/40',
      false: '',
    },
  },
  defaultVariants: {
    selected: 'false',
    disabled: 'false',
    focused: 'false',
  },
});

@Component({
  selector: 'ui-select-glass',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, IconGlassComponent, OverlayModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectGlassComponent),
      multi: true,
    },
  ],
  template: `
    <div class="space-y-2">
      <!-- Label -->
      @if (label && showLabel) {
        <label [for]="selectId" class="block text-sm font-medium text-slate-300 dark:text-slate-200">
          {{ label }}
          @if (required) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }

      <!-- Select Trigger -->
      <div class="relative">
        <div
          #triggerRef
          [id]="selectId"
          [class]="computedSelectClasses()"
          (click)="toggle()"
          (keydown)="handleKeyDown($event)"
          [attr.aria-expanded]="isOpen()"
          [attr.aria-haspopup]="'listbox'"
          [attr.aria-controls]="selectId + '-listbox'"
          [attr.aria-disabled]="disabled"
          tabindex="0"
          role="combobox"
          cdkOverlayOrigin
          #overlayOrigin="cdkOverlayOrigin"
        >
          <!-- Selected value display -->
          <div class="flex items-center flex-1 relative z-10">
            <!-- Selected option icon -->
            @if (selectedIcon()) {
              <span class="mr-3 shrink-0">
                <ui-icon [name]="selectedIcon()" size="sm"></ui-icon>
              </span>
            }

            <!-- Selected text or placeholder -->
            <span class="flex-1 truncate" [class.opacity-50]="!selectedOption()">
              {{ selectedOption()?.label || placeholder }}
            </span>
          </div>

          <!-- Chevron icon -->
          <div class="ml-2 shrink-0 relative z-10">
            <ui-icon
              name="chevron-down"
              size="sm"
              [customClass]="'transition-transform duration-300 ' + (isOpen() ? 'rotate-180' : '')"
            ></ui-icon>
          </div>

          <!-- Glass shimmer effect -->
          <div class="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none">
            <div
              class="absolute inset-0 bg-linear-to-r from-transparent via-white/12 to-transparent
                        transform -skew-x-12 -translate-x-full group-hover:translate-x-full
                        transition-transform duration-1000 ease-out rounded-xl"
            ></div>
          </div>
        </div>
      </div>

      <!-- Help text -->
      @if (helpText && !errorMessage) {
        <p class="text-sm text-slate-400 dark:text-slate-300">{{ helpText }}</p>
      }

      <!-- Error message -->
      @if (errorMessage) {
        <p class="text-sm text-red-600 dark:text-red-400 flex items-center">
          <ui-icon name="alert-circle" size="sm" customClass="mr-1"></ui-icon>
          {{ errorMessage }}
        </p>
      }

      <!-- Success message -->
      @if (successMessage) {
        <p class="text-sm text-cyan-600 dark:text-cyan-400 flex items-center">
          <ui-icon name="check" size="sm" customClass="mr-1"></ui-icon>
          {{ successMessage }}
        </p>
      }
    </div>

    <!-- Dropdown Template (rendered in overlay) -->
    <ng-template #dropdownTemplate>
      <!-- Backdrop (rendered first, behind dropdown) -->
      <div class="fixed inset-0 bg-black/10 backdrop-blur-sm -z-10" (click)="close()"></div>

      <div [class]="computedDropdownClasses()" [style.width.px]="triggerWidth">
        <!-- Search input (if searchable) -->
      @if (searchable) {
          <div
            [class]="searchHeaderClasses()"
          >
            <div class="relative">
              <input
                #searchInput
                type="text"
                [value]="searchQuery()"
                (input)="updateSearchQuery($event)"
                [placeholder]="searchPlaceholder"
                [class]="searchInputClasses()"
                (click)="$event.stopPropagation()"
              />
              <div class="absolute left-2 top-1/2 transform -translate-y-1/2">
                <ui-icon name="search" size="sm" [customClass]="searchIconClasses()"></ui-icon>
              </div>
            </div>
          </div>
        }

        <!-- Options list -->
        <div class="relative z-10 py-1 max-h-48 overflow-y-auto" [id]="selectId + '-listbox'" role="listbox">
          @for (option of filteredOptions(); track option.value; let i = $index) {
            <div
              [class]="getOptionClasses(option, i)"
              [id]="selectId + '-option-' + i"
              (click)="selectOption(option)"
              [attr.aria-selected]="isSelected(option)"
              role="option"
            >
              <!-- Option icon -->
              @if (option.icon) {
                <span class="mr-3 shrink-0">
                  <ui-icon [name]="getOptionIcon(option)" size="sm"></ui-icon>
                </span>
              }

              <!-- Option content -->
              <div class="flex-1 min-w-0">
                <div class="font-medium truncate">{{ option.label }}</div>
                @if (option.description) {
                  <div class="text-sm opacity-75 truncate">{{ option.description }}</div>
                }
              </div>

              <!-- Selected indicator -->
              @if (isSelected(option)) {
                <div class="ml-2 shrink-0">
                  <ui-icon name="check" size="sm" customClass="text-primary-500"></ui-icon>
                </div>
              }

              <!-- Option hover glass effect -->
              <div
              class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300
                            bg-linear-to-r from-transparent via-white/8 to-transparent rounded-md pointer-events-none"
              ></div>
            </div>
          }

          <!-- No options message -->
          @if (filteredOptions().length === 0) {
            <div class="px-4 py-6 text-center text-slate-500 dark:text-slate-400">
              <ui-icon name="search" size="lg" customClass="mx-auto mb-2 opacity-50"></ui-icon>
              <p>{{ noOptionsMessage }}</p>
            </div>
          }
        </div>

        <!-- Premium floating particles -->
        @if (variant === 'premium') {
          <div class="absolute inset-0 overflow-hidden pointer-events-none opacity-40">
            <div class="absolute top-1/4 left-1/4 w-1 h-1 bg-primary-400/60 rounded-full animate-pulse"></div>
            <div class="absolute top-3/4 right-1/4 w-0.5 h-0.5 bg-white/40 rounded-full animate-pulse delay-1000"></div>
            <div
              class="absolute bottom-1/3 left-1/2 w-1.5 h-1.5 bg-primary-300/40 rounded-full animate-pulse delay-2000"
            ></div>
          </div>
        }

        <!-- Glass border accents -->
        <div class="absolute inset-0 rounded-xl opacity-60 pointer-events-none -z-10">
          <div
            class="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-primary-400/50 to-transparent"
          ></div>
          <div
            class="absolute inset-x-0 bottom-0 h-px bg-linear-to-r from-transparent via-primary-400/30 to-transparent"
          ></div>
        </div>
      </div>
    </ng-template>
  `,
  host: {
    class: 'relative block w-full',
  },
})
export class SelectGlassComponent<T = unknown> implements ControlValueAccessor, OnDestroy {
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private themeService = inject(ThemeService);

  @ViewChild('triggerRef') triggerRef!: ElementRef<HTMLElement>;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<unknown> | null;
  @ViewChild('overlayOrigin', { read: ElementRef }) overlayOrigin!: ElementRef<HTMLElement>;

  @Input() options: SelectOption<T>[] = [];
  @Input() label: string = '';
  @Input() showLabel: boolean = false;
  @Input() placeholder: string = 'Select an option...';
  @Input() variant: SelectVariant = 'default';
  @Input() size: SelectSize = 'md';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() multiple: boolean = false;
  @Input() searchable: boolean = false;
  @Input() searchPlaceholder: string = 'Search options...';
  @Input() helpText: string = '';
  @Input() errorMessage: string = '';
  @Input() successMessage: string = '';
  @Input() noOptionsMessage: string = 'No options found';
  @Input() themeSection: ThemeSection = 'global';

  @Input() set value(val: T | T[] | null) {
    this.selectedValue.set(val);
  }
  get value(): T | T[] | null {
    return this.selectedValue();
  }

  @Output() selectionChange = new EventEmitter<SelectOption<T> | SelectOption<T>[]>();

  protected selectId = `select-${Math.random().toString(36).substring(2, 11)}`;
  protected isOpen = signal(false);
  protected searchQuery = signal('');
  public selectedValue = signal<T | T[] | null>(null);
  protected focusedIndex = signal(-1);
  protected triggerWidth = 0;

  private overlayRef: OverlayRef | null = null;

  // ControlValueAccessor
  private onChange: (value: T | T[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  protected selectedOption = computed(() => {
    const value = this.selectedValue();
    if (value === null || value === undefined) return null;
    if (this.multiple) return null; // Handle multiple selection display separately
    return this.options.find((option) => option.value === value) || null;
  });

  /** Returns the selected option's icon name or empty string. Always returns string to satisfy IconGlassComponent.name. */
  protected selectedIcon = computed(() => {
    const opt = this.selectedOption();
    return opt && opt.icon ? opt.icon : '';
  });

  /** Helper returning a definite string for option icons. Use in templates to avoid passing `string | undefined`. */
  protected getOptionIcon(option: SelectOption<T>): string {
    return option.icon ?? '';
  }

  protected filteredOptions = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) return this.options;

    return this.options.filter(
      (option) => option.label.toLowerCase().includes(query) || option.description?.toLowerCase().includes(query),
    );
  });

  public computedSelectClasses = computed(() => {
    const state = this.errorMessage ? 'error' : this.successMessage ? 'success' : 'default';
    return [
      selectVariants({
        variant: this.variant,
        size: this.size,
        state,
      }),
      this.getSelectThemeClasses(),
    ].join(' ');
  });

  public computedDropdownClasses = computed(() => {
    return [
      dropdownVariants({
        variant: this.variant,
        state: this.isOpen() ? 'open' : 'closed',
      }),
      this.getDropdownThemeClasses(),
    ].join(' ');
  });

  public getOptionClasses(option: SelectOption<T>, index: number): string {
    const isSelected = this.isSelected(option);
    const isFocused = this.focusedIndex() === index;

    return [
      optionVariants({
        selected: isSelected ? 'true' : 'false',
        disabled: option.disabled ? 'true' : 'false',
        focused: isFocused ? 'true' : 'false',
      }),
      this.getOptionThemeClasses(isSelected, isFocused),
    ].join(' ');
  }

  public isSelected(option: SelectOption<T>): boolean {
    const value = this.selectedValue();
    if (this.multiple && Array.isArray(value)) {
      return value.includes(option.value);
    }
    return value === option.value;
  }

  protected resolvedTheme(): 'light' | 'dark' {
    if (this.themeSection === 'global') {
      return this.themeService.globalTheme();
    }

    return this.themeService.currentThemes()[this.themeSection];
  }

  protected searchHeaderClasses(): string {
    const base = 'p-3 border-b backdrop-blur-sm relative z-10';
    return this.resolvedTheme() === 'dark'
      ? `${base} !border-slate-200/12 !bg-[#14243b]/88`
      : `${base} !border-slate-300/40 !bg-white/94`;
  }

  protected searchInputClasses(): string {
    const base =
      'w-full h-8 px-3 pl-8 rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-300/40 backdrop-blur-sm transition-all duration-200';
    return this.resolvedTheme() === 'dark'
      ? `${base} !bg-[#0f1c30]/82 !border-slate-200/16 !text-slate-100 placeholder:!text-slate-400`
      : `${base} !bg-white/96 !border-slate-300/50 !text-slate-700 placeholder:!text-slate-400`;
  }

  protected searchIconClasses(): string {
    return this.resolvedTheme() === 'dark' ? '!text-slate-400' : '!text-slate-400';
  }

  protected updateSearchQuery(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchQuery.set(target.value);
  }

  open(): void {
    if (this.disabled || this.overlayRef) return;

    // Store trigger width for dropdown sizing
    if (this.triggerRef && this.triggerRef.nativeElement) {
      this.triggerWidth = this.triggerRef.nativeElement.offsetWidth;
    } else {
      this.triggerWidth = 0;
    }

    // Create overlay
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.triggerRef)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 4,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -4,
        },
      ])
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      width: this.triggerWidth,
      panelClass: 'ui-select-overlay-pane',
    });

    // Attach the template portal
    if (!this.dropdownTemplate) {
      // Template not available yet; bail out
      return;
    }

    const portal = new TemplatePortal<unknown>(this.dropdownTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);

    // Close when clicking outside
    this.overlayRef.backdropClick().subscribe(() => {
      this.close();
    });

    this.isOpen.set(true);
    this.focusedIndex.set(-1);

    // Focus search input if searchable
    if (this.searchable) {
      setTimeout(() => {
        this.searchInput?.nativeElement?.focus();
      }, 100);
    }
  }

  close(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.isOpen.set(false);
    this.searchQuery.set('');
    this.focusedIndex.set(-1);
    this.onTouched();
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  public selectOption(option: SelectOption<T>): void {
    if (option.disabled) return;

    if (this.multiple) {
      const currentValue = (this.selectedValue() as T[]) || [];
      const newValue = currentValue.includes(option.value)
        ? currentValue.filter((v) => v !== option.value)
        : [...currentValue, option.value];

      this.selectedValue.set(newValue);
      this.onChange(newValue);
      this.selectionChange.emit(this.options.filter((opt) => newValue.includes(opt.value)));
    } else {
      this.selectedValue.set(option.value);
      this.onChange(option.value);
      this.selectionChange.emit(option);
      this.close();
    }
  }

  protected handleKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.isOpen() && this.focusedIndex() >= 0) {
          const options = this.filteredOptions();
          const focused = options[this.focusedIndex()];
          if (focused && !focused.disabled) {
            this.selectOption(focused);
          }
        } else {
          this.toggle();
        }
        break;
      case 'Escape':
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen()) {
          this.open();
          this.focusedIndex.set(this.findNextEnabledIndex(-1, 1));
        } else {
          this.focusedIndex.set(this.findNextEnabledIndex(this.focusedIndex(), 1));
        }
        this.scrollFocusedIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen()) {
          this.open();
          this.focusedIndex.set(this.findNextEnabledIndex(this.filteredOptions().length, -1));
        } else {
          this.focusedIndex.set(this.findNextEnabledIndex(this.focusedIndex(), -1));
        }
        this.scrollFocusedIntoView();
        break;
    }
  }

  /**
   * Finds the next non-disabled option index in the given direction, wrapping around.
   * Returns -1 if no enabled option exists.
   */
  private findNextEnabledIndex(fromIndex: number, direction: 1 | -1): number {
    const options = this.filteredOptions();
    if (options.length === 0) return -1;

    let index = fromIndex + direction;
    let checked = 0;

    while (checked < options.length) {
      // Wrap around
      if (index >= options.length) index = 0;
      if (index < 0) index = options.length - 1;

      if (!options[index]?.disabled) {
        return index;
      }

      index += direction;
      checked++;
    }

    // All options are disabled
    return -1;
  }

  /** Scrolls the currently focused option into the visible area of the dropdown. */
  private scrollFocusedIntoView(): void {
    const index = this.focusedIndex();
    if (index < 0) return;

    // Use setTimeout to allow the DOM to update after signal change
    setTimeout(() => {
      const element = document.getElementById(`${this.selectId}-option-${index}`);
      element?.scrollIntoView({ block: 'nearest' });
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: T | T[] | null): void {
    this.selectedValue.set(value);
  }

  registerOnChange(fn: (value: T | T[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private getSelectThemeClasses(): string {
    const isDark = this.resolvedTheme() === 'dark';

    if (isDark) {
      switch (this.variant) {
        case 'elevated':
          return '!bg-linear-to-b !from-[#1f3554]/78 !to-[#13233a]/90 !border-primary-200/35 !text-slate-50';
        case 'premium':
          return '!bg-linear-to-r !from-white/15 !to-primary-900/20 !border-primary-400/30 !text-gray-100';
        case 'minimal':
          return '!bg-white/5 !border-gray-600/15 !text-gray-300';
        case 'default':
        default:
          return '!bg-linear-to-b !from-[#1c2e4a]/72 !to-[#101d31]/84 !border-slate-200/25 !text-slate-100';
      }
    }

    switch (this.variant) {
      case 'elevated':
        return '!bg-linear-to-b !from-slate-100/98 !to-slate-200/95 !border-primary-300/40 !text-slate-700';
      case 'premium':
        return '!bg-linear-to-r !from-white/25 !to-primary-50/20 !border-primary-300/40 !text-gray-800';
      case 'minimal':
        return '!bg-white/10 !border-gray-200/20 !text-gray-600';
      case 'default':
      default:
        return '!bg-linear-to-b !from-slate-100/95 !to-slate-200/92 !border-slate-400/35 !text-slate-700';
    }
  }

  private getDropdownThemeClasses(): string {
    const isDark = this.resolvedTheme() === 'dark';

    if (isDark) {
      switch (this.variant) {
        case 'elevated':
          return '!bg-linear-to-b !from-[#274165]/94 !to-[#16263f]/98 !border-primary-200/28';
        case 'premium':
          return '!bg-linear-to-br !from-white/20 !via-white/10 !to-primary-900/30 !border-primary-400/40';
        case 'minimal':
          return '!bg-linear-to-b !from-[#223652]/88 !to-[#122034]/94 !border-slate-200/18';
        case 'default':
        default:
          return '!bg-linear-to-b !from-[#243a5c]/92 !to-[#13233a]/96 !border-slate-200/22';
      }
    }

    switch (this.variant) {
      case 'elevated':
        return '!bg-linear-to-b !from-white/98 !to-slate-100/96 !border-primary-300/45';
      case 'premium':
        return '!bg-linear-to-br !from-white/80 !via-white/70 !to-primary-50/40 !border-primary-200/60';
      case 'minimal':
        return '!bg-linear-to-b !from-white/96 !to-slate-100/94 !border-slate-300/45';
      case 'default':
      default:
        return '!bg-linear-to-b !from-white/98 !to-slate-100/96 !border-slate-300/55';
    }
  }

  private getOptionThemeClasses(isSelected: boolean, isFocused: boolean): string {
    const isDark = this.resolvedTheme() === 'dark';

    if (isDark) {
      if (isSelected) {
        return '!bg-primary-800/30 !text-primary-200';
      }

      if (isFocused) {
        return '!bg-white/15 !text-gray-100 ring-1 ring-primary-400/40';
      }

      return '!text-gray-100 hover:!bg-white/10 focus:!bg-white/10';
    }

    if (isSelected) {
      return '!bg-primary-100/50 !text-primary-700';
    }

    if (isFocused) {
      return '!bg-slate-200/70 !text-slate-700 ring-1 ring-primary-400/40';
    }

    return '!text-slate-700 hover:!bg-slate-100/80 focus:!bg-slate-100/80';
  }
}

/**
 * Usage Examples:
 *
 * Basic select:
 * <ui-select-glass
 *   label="Country"
 *   [options]="countries"
 *   placeholder="Select your country"
 * ></ui-select-glass>
 *
 * Premium e-commerce select with search:
 * <ui-select-glass
 *   label="Product Category"
 *   variant="premium"
 *   size="lg"
 *   [searchable]="true"
 *   [options]="categories"
 *   (selectionChange)="onCategoryChange($event)"
 * ></ui-select-glass>
 *
 * Multi-select with icons:
 * <ui-select-glass
 *   label="Features"
 *   [multiple]="true"
 *   [options]="features"
 *   variant="elevated"
 * ></ui-select-glass>
 *
 * Options format:
 * categories: SelectOption[] = [
 *   { value: 'electronics', label: 'Electronics', icon: 'smartphone' },
 *   { value: 'clothing', label: 'Clothing', icon: 'shirt', description: 'Fashion & Apparel' },
 *   { value: 'books', label: 'Books', icon: 'book' }
 * ];
 */
